/**
 * Theme store.
 *
 * Persists the user's preference to localStorage so every device/PC can
 * keep its own mode. Supports `light`, `dark` and `system` (smart) modes;
 * `system` follows the OS `prefers-color-scheme` setting live.
 */

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'firstaid-os-theme';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

type Listener = () => void;
const listeners = new Set<Listener>();

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readStoredTheme(): Theme {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    if (isTheme(value)) return value;
  } catch {
    /* localStorage may be unavailable (private mode / SSR) */
  }
  return 'system';
}

let currentTheme: Theme = typeof window === 'undefined' ? 'system' : readStoredTheme();

export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
}

export function resolveTheme(theme: Theme = currentTheme): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme;
}

export function getTheme(): Theme {
  return currentTheme;
}

/** Apply the resolved theme to the document without persisting. */
export function applyTheme(theme: Theme = currentTheme): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(theme);
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

/** Persist and apply a new theme preference. */
export function setTheme(theme: Theme): void {
  currentTheme = theme;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* ignore write failures */
  }
  applyTheme(theme);
  notify();
}

/** Cycle through light -> dark -> system. */
export function cycleTheme(): Theme {
  const order: Theme[] = ['light', 'dark', 'system'];
  const next = order[(order.indexOf(currentTheme) + 1) % order.length];
  setTheme(next);
  return next;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Keep the document, system preference and other tabs in sync. */
export function initTheme(): void {
  if (typeof window === 'undefined') return;

  applyTheme(currentTheme);

  window.matchMedia(MEDIA_QUERY).addEventListener('change', () => {
    if (currentTheme === 'system') {
      applyTheme(currentTheme);
      notify();
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== THEME_STORAGE_KEY) return;
    currentTheme = readStoredTheme();
    applyTheme(currentTheme);
    notify();
  });
}
